<template>
	<div class="popup-overlay">
		<div class="top-bar">
			<button class="close-button" @click="handleClose">
				<InlineSvg src="/images/icons-2025/x-close.svg" />
			</button>
			<div v-if="imageType === 'marketing-post'" class="action-buttons">
				<button class="share-button" @click="onShare" :disabled="!imageLoaded || imageError">
					<InlineSvg src="/images/icons-2025/share.svg" />
				</button>
				<button class="download-button" @click="onDownload" :disabled="!imageLoaded || imageError">
					<InlineSvg src="/images/icons-2025/download.svg" />
				</button>
			</div>
		</div>

		<div class="image-container">
			<img :src="imageSrc" :alt="imageAlt || 'Chat image'" class="chat-image" @load="onImageLoad" @error="onImageError" />
		</div>
	</div>
</template>

<script>
	import ImageActionsMixin from './mixins/ImageActionsMixin.js'

	export default {
		name: 'ChatImageViewer',
		mixins: [ImageActionsMixin],
		props: {
			imageSrc: {
				type: String,
				required: true,
			},
			imageAlt: {
				type: String,
				default: '',
			},
			imageFilename: {
				type: String,
				default: 'chat-image',
			},
			imageType: {
				type: String,
				default: 'marketing-post', // 'marketing-post' or 'attachment'
			},
		},
		data() {
			return {
				imageLoaded: false,
				imageError: false,
			}
		},
		mounted() {
			// Track image viewer opened event
			const eventName = this.imageType === 'attachment' ? 'chat-v2-attachment-image-view' : 'chat-v2-marketing-post-view'
			const triggerLocation = this.imageType === 'attachment' ? 'chat-attachment-image-viewer' : 'chat-image-viewer'

			this.$stanAnalytics(eventName, {
				meta: { user_id: this.user?.user_id, username: this.user?.username },
				props: {
					image_src: this.imageSrc,
					image_alt: this.imageAlt,
					image_filename: this.imageFilename,
					trigger_location: triggerLocation,
				},
			})
		},
		methods: {
			handleClose() {
				this.$emit('close')
			},
			async onShare() {
				if (!this.imageLoaded || this.imageError) {
					return
				}

				// Track share clicked event
				this.$stanAnalytics('chat-v2-marketing-post-share', {
					meta: { user_id: this.user?.user_id, username: this.user?.username },
					props: {
						image_src: this.imageSrc,
						image_alt: this.imageAlt,
						image_filename: this.imageFilename,
						trigger_location: 'chat-image-viewer',
					},
				})
				this.$axios.post('/v1/users/onboarding_dashboard_marketing_post_created', {})

				await this.handleShare(this.imageSrc, this.imageAlt, this.imageFilename)
			},
			onDownload() {
				if (!this.imageLoaded || this.imageError) {
					return
				}

				// Track download clicked event
				this.$stanAnalytics('chat-v2-marketing-post-download', {
					meta: { user_id: this.user?.user_id, username: this.user?.username },
					props: {
						image_src: this.imageSrc,
						image_alt: this.imageAlt,
						image_filename: this.imageFilename,
						trigger_location: 'chat-image-viewer',
					},
				})
				this.$axios.post('/v1/users/onboarding_dashboard_marketing_post_created', {})

				this.handleDownload(this.imageSrc, this.imageFilename)
			},
			onImageLoad() {
				this.imageLoaded = true
				this.imageError = false
			},
			onImageError() {
				this.imageLoaded = false
				this.imageError = true
			},
		},
	}
</script>

<style lang="scss" scoped>
	.popup-overlay {
		position: fixed;
		top: 0;
		left: 0;
		width: 100vw;
		height: 100vh;
		@supports (height: 100dvh) {
			height: 100dvh;
		}
		background-color: #f9f9ff;
		z-index: 4999;
		display: flex;
		flex-direction: column;
	}

	.top-bar {
		display: flex;
		justify-content: space-between;
		padding: 16px 12px 0px 12px;
		z-index: 1;
		position: relative;
	}

	.action-buttons {
		display: flex;
		gap: 8px;
	}

	.close-button,
	.share-button,
	.download-button {
		background: rgba(255, 255, 255, 0.1);
		border: none;
		border-radius: 50%;
		width: 40px;
		height: 40px;
		display: flex;
		align-items: center;
		justify-content: center;
		cursor: pointer;
		transition: background-color 0.2s ease;

		&:hover {
			background: rgba(255, 255, 255, 0.2);
		}

		&:disabled {
			opacity: 0.5;
			cursor: not-allowed;
		}

		:deep(svg) {
			width: 32px;
			height: 32px;
			fill: white;
		}
	}

	.image-container {
		flex: 1;
		display: flex;
		align-items: flex-start;
		justify-content: center;
		padding: 40px;
		overflow: hidden;
	}

	.chat-image {
		max-width: 100%;
		max-height: 100%;
		object-fit: contain;
	}
</style>
